/**
 * Modal Block - Interactivity API View Script
 *
 * Handles triggers (click/timer/scroll/anchor), open/close,
 * focus trap, body scroll lock, and show-once via localStorage.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

// Focusable element selector for focus trap.
const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Lock body scroll and compensate for scrollbar width.
 */
function lockScroll() {
	const scrollbarWidth =
		window.innerWidth - document.documentElement.clientWidth;
	document.documentElement.style.overflow = 'hidden';
	document.documentElement.style.paddingRight = `${ scrollbarWidth }px`;
}

/**
 * Unlock body scroll.
 */
function unlockScroll() {
	document.documentElement.style.overflow = '';
	document.documentElement.style.paddingRight = '';
}

/**
 * Check if modal was already shown (show-once feature).
 */
function wasShown( modalId ) {
	try {
		return localStorage.getItem( `brandy-modal-shown-${ modalId }` ) === '1';
	} catch {
		return false;
	}
}

/**
 * Mark modal as shown in localStorage.
 */
function markShown( modalId ) {
	try {
		localStorage.setItem( `brandy-modal-shown-${ modalId }`, '1' );
	} catch {
		// Private browsing — fail silently.
	}
}

/**
 * Open modal using pre-captured context and element ref.
 * Safe to call from async callbacks (setTimeout, scroll, anchor click)
 * where getContext()/getElement() are unavailable.
 */
function openModal( ctx, ref ) {
	if ( ctx.isOpen ) {
		return;
	}
	ctx.isOpen = true;
	lockScroll();

	// Focus the dialog after DOM update.
	requestAnimationFrame( () => {
		const dialog = ref.querySelector( '.brandy-modal-dialog' );
		if ( dialog ) {
			const firstFocusable =
				dialog.querySelector( FOCUSABLE_SELECTOR );
			if ( firstFocusable ) {
				firstFocusable.focus();
			} else {
				dialog.setAttribute( 'tabindex', '-1' );
				dialog.focus();
			}
		}
	} );

}

store( 'brandy/modal', {
	state: {
		get isOpen() {
			return getContext().isOpen;
		},
	},
	actions: {
		open() {
			openModal( getContext(), getElement().ref );
		},

		close() {
			const ctx = getContext();
			if ( ! ctx.isOpen ) {
				return;
			}
			ctx.isOpen = false;
			unlockScroll();

			// Mark shown on close for show-once.
			if ( ctx.showOnce ) {
				markShown( ctx.modalId );
			}

			// Return focus to trigger element.
			if ( ctx._returnFocus ) {
				ctx._returnFocus.focus();
				ctx._returnFocus = null;
			}
		},

		handleBackdropClick( event ) {
			const ctx = getContext();
			if ( ! ctx.closeOnBackdropClick ) {
				return;
			}
			if ( event.target.classList.contains( 'brandy-modal-backdrop' ) ) {
				const { actions } = store( 'brandy/modal' );
				actions.close();
			}
		},

		handleKeyDown( event ) {
			const ctx = getContext();
			if ( ! ctx.isOpen ) {
				return;
			}

			// ESC to close.
			if ( event.key === 'Escape' && ctx.closeOnEsc ) {
				const { actions } = store( 'brandy/modal' );
				actions.close();
				return;
			}

			// Focus trap: Tab / Shift+Tab.
			if ( event.key === 'Tab' ) {
				const { ref } = getElement();
				const dialog = ref.querySelector( '.brandy-modal-dialog' );
				if ( ! dialog ) {
					return;
				}

				const focusable = dialog.querySelectorAll( FOCUSABLE_SELECTOR );
				if ( ! focusable.length ) {
					return;
				}

				const first = focusable[ 0 ];
				const last = focusable[ focusable.length - 1 ];

				if ( event.shiftKey && document.activeElement === first ) {
					event.preventDefault();
					last.focus();
				} else if (
					! event.shiftKey &&
					document.activeElement === last
				) {
					event.preventDefault();
					first.focus();
				}
			}
		},
	},
	callbacks: {
		init() {
			const ctx = getContext();
			const { ref } = getElement();

			// Show-once check: bail if already dismissed.
			if ( ctx.showOnce && wasShown( ctx.modalId ) ) {
				return;
			}

			// Timer trigger.
			if ( ctx.triggerType === 'timer' ) {
				ctx._timerId = setTimeout( () => {
					openModal( ctx, ref );
				}, ctx.timerDelay );
			}

			// Scroll depth trigger.
			if ( ctx.triggerType === 'scroll' ) {
				const scrollHandler = () => {
					const docHeight =
						document.documentElement.scrollHeight -
						window.innerHeight;
					if ( docHeight <= 0 ) {
						return;
					}
					const scrollPercent =
						( window.scrollY / docHeight ) * 100;
					if ( scrollPercent >= ctx.scrollDepth ) {
						openModal( ctx, ref );
						window.removeEventListener( 'scroll', scrollHandler );
					}
				};
				window.addEventListener( 'scroll', scrollHandler, {
					passive: true,
				} );
				ctx._scrollCleanup = () =>
					window.removeEventListener( 'scroll', scrollHandler );
			}

			// Anchor link support: [href="#modalId"] or [data-modal-target="modalId"].
			if ( ctx.modalId ) {
				const anchors = document.querySelectorAll(
					`a[href="#${ ctx.modalId }"], [data-modal-target="${ ctx.modalId }"]`
				);
				const anchorHandler = ( e ) => {
					e.preventDefault();
					ctx._returnFocus = e.currentTarget;
					openModal( ctx, ref );
				};
				anchors.forEach( ( a ) =>
					a.addEventListener( 'click', anchorHandler )
				);
				ctx._anchorCleanup = () =>
					anchors.forEach( ( a ) =>
						a.removeEventListener( 'click', anchorHandler )
					);
			}

			// Click trigger: save return focus on trigger button click.
			if ( ctx.triggerType === 'click' ) {
				const trigger = ref.querySelector( '.brandy-modal-trigger' );
				if ( trigger ) {
					trigger.addEventListener( 'click', () => {
						ctx._returnFocus = trigger;
					} );
				}
			}

			// Cleanup on unmount.
			return () => {
				clearTimeout( ctx._timerId );
				ctx._scrollCleanup?.();
				ctx._anchorCleanup?.();
				if ( ctx.isOpen ) {
					unlockScroll();
				}
			};
		},
	},
} );
